import { AskRequest, ReportType, ReportTypes } from '@hone-automation/common';
import { detectAnomaly } from './detectAnomalies';
import { Amounts, ReportViewModel, VizHelpers, Anomaly } from './types';
import { series } from './utils';
import { getQueueCells } from '@hone-automation/common';

type ParseDataRowsProps = {
  section: any;
  transformedData: ReportViewModel;
  filteredId: string;
  path: string[];
  sectionIndex: number;
  title: string;
  refTitle: string;
  vizHelpers: VizHelpers;
  reportType?: ReportType;
};

export const parseDataRows = (dataRowsObject: ParseDataRowsProps) => {
  const { section, transformedData, filteredId, path, sectionIndex, title, vizHelpers, refTitle, reportType } =
    dataRowsObject;
  const amounts: Amounts[] = [];

  // We look for anomalies in the data, both in amounts and transactions
  const anomaly = detectAnomaly(series(section.data, 'amount'));
  const trxAnomaly = detectAnomaly(series(section.data, 'dataRows'));

  // Use data exactly as received from backend - no transformations
  section.data.forEach((data: any, index: number) => {
    const accountId = `${filteredId}_@_${sectionIndex}`;
    const queueIssues = getQueueCells(transformedData);
    const trxFlagged = queueIssues?.filter((trx: AskRequest) => {
      // AskRequest format: identifier (account name), flaggedAmount (amount)
      const trxAccount = trx.identifier;
      const trxAmount = trx.flaggedAmount;

      // Primary: Match by eventId/externalId AND verify the account matches
      // EventId alone is not unique - journal entries can touch multiple accounts
      const accountMatches = trxAccount === section.title || trxAccount === section.title.split(' ').slice(1).join(' '); // Handle prefix stripping for BS/CF

      const hasEventIdMatch =
        accountMatches &&
        data.dataRows?.some(
          (dR: any) => dR.eventId && trx.transaction?.externalId && dR.eventId === trx.transaction.externalId
        );

      if (hasEventIdMatch) return true;

      // Fallback: Match by transaction details (type, date, amount, memo)
      const hasMatchingTransactions = data.dataRows?.some(
        (dR: any) =>
          trx.transaction?.trxType === dR.type &&
          trx.period === dR.txnDate &&
          trxAmount === dR.amount &&
          trx.transaction?.memo === dR.description
      );
      let isCellFlagged = false;
      // Otherwise we check the flagged elements vs the cell itself (type = Flagged Cell)
      if (reportType === ReportTypes.BalanceSheet || reportType === ReportTypes.CashFlow) {
        isCellFlagged =
          trxAccount === section.title.split(' ').slice(1).join(' ') &&
          transformedData.columns[index]?.date?.start === trx.period;
      } else {
        if (typeof transformedData.columns[index] !== 'undefined') {
          isCellFlagged = trxAccount === section.title && transformedData.columns[index]?.date?.start === trx.period;
        }
      }

      return hasMatchingTransactions || isCellFlagged;
    });

    const amount = getDataAmountsColumn({
      data,
      date: (transformedData.columns[index] && transformedData.columns[index].date?.start) || '',
      reportId: transformedData.columns[index]?.reportId,
      title,
      refTitle,
      path,
      baseRef: section.titlePerc !== '' ? section.titlePerc : undefined,
      index,
      accountId,
      vizHelpers: vizHelpers,
      anomaly,
      trxAnomaly,
      trxFlagged,
    });
    amounts.push(amount);
  });

  return { anomaly, amounts };
};

const getDataAmountsColumn = ({
  data,
  accountId,
  index,
  reportId,
  ...rest
}: {
  data: any;
  reportId?: string;
  accountId: string;
  index: number;
  date: string;
  title: string;
  refTitle: string;
  path: string[] | undefined;
  baseRef: string;
  vizHelpers: VizHelpers;
  anomaly: Anomaly;
  trxAnomaly: Anomaly;
  trxFlagged?: AskRequest[];
}): Amounts => {
  return {
    ...rest,
    ...data,
    reportId,
    amountAVG: data.amtAvg,
    percent: data.perc,
    percentAVG: data.percAvg,
    cellID: `${accountId}_-x-_${index + 1}`,
    baseAmount: data.percent ? data.amount / data.perc : data.amount,
    transactions: data.dataRows.length || 0,
    id: accountId,
    refTitle: data.refTitle,
    index,
    // Explicitly set trxFlagged after spreads to prevent data.trxFlagged from overwriting the computed value
    trxFlagged: rest.trxFlagged || [],
  };
};
